import type { PropsWithChildren } from 'react';
import { I18nManager, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useDrawerProgress } from '@react-navigation/drawer';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { useTheme } from '../../hooks/useTheme';
import { DRAWER_VISUAL_SPEC, getDrawerWidth } from './drawerVisualSpec';

/** Native screen containers clip exterior shadows. Paint the shadow behind the
 * moving scene in the drawer layer, without disabling inactive-screen detaching. */
export function DrawerRevealShadow({ children }: PropsWithChildren) {
  const progress = useDrawerProgress();
  const { width } = useWindowDimensions();
  const drawerWidth = getDrawerWidth(width);
  const { theme, isDark } = useTheme();
  const shadowStyle = useAnimatedStyle(() => {
    const amount = Math.max(0, Math.min(1, progress.value));
    return {
      borderRadius: amount * DRAWER_VISUAL_SPEC.openPanelRadius,
      opacity: amount,
      transform: [{ translateX: (I18nManager.isRTL ? -1 : 1) * drawerWidth * amount }],
    };
  });
  return (
    <View style={styles.backdrop}>
      {children}
      <Animated.View
        pointerEvents="none"
        accessible={false}
        style={[
          styles.shadow,
          styles.shadowPlane,
          { width, backgroundColor: theme.background, shadowOpacity: isDark ? 0.32 : 0.12 },
          shadowStyle,
        ]}
      />
    </View>
  );
}

/** The drawer owns translation and gestures; this only decorates its moving scene. */
export function DrawerScenePanel({ children }: PropsWithChildren) {
  const progress = useDrawerProgress();
  const { theme } = useTheme();
  const clippingStyle = useAnimatedStyle(() => ({
    borderRadius: Math.max(0, Math.min(1, progress.value)) * DRAWER_VISUAL_SPEC.openPanelRadius,
  }));
  const washStyle = useAnimatedStyle(() => ({
    opacity: Math.max(0, Math.min(1, progress.value)) * DRAWER_VISUAL_SPEC.openPanelWash,
  }));

  return (
    <Animated.View style={[styles.clip, { backgroundColor: theme.background }, clippingStyle]}>
      {children}
      {/* Fade a separate surface, never the native glass or its ancestors.
          The navigator's transparent overlay still owns tap-to-close/a11y. */}
      <Animated.View
        pointerEvents="none"
        accessible={false}
        style={[StyleSheet.absoluteFill, { backgroundColor: theme.background }, washStyle]}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1 },
  shadowPlane: { position: 'absolute', top: 0, bottom: 0, start: 0 },
  shadow: {
    flex: 1,
    shadowColor: '#000000',
    shadowRadius: 18,
    shadowOffset: { width: I18nManager.isRTL ? 4 : -4, height: 0 },
  },
  clip: { flex: 1, overflow: 'hidden', borderCurve: 'continuous' },
});
